import { STORE_KEY_LIST } from 'Constants/'
import { fromUTCtoZoneFormatted } from 'Helpers/dates'
import { storeGet } from 'Helpers/localStorage'
import {
  modalEmailMessagesSearchState,
  modalEmailMessagesState,
  tableColumns
} from 'Hooks/useModalEmailMessages/useModalEmailMessages'
import { notifyParent } from 'mailup-react-helpers'
import {
  ACTION_BUTTON_TYPE_SELECT,
  ButtonAction,
  DataListExtraColumn,
  DataListHeader,
  DataListRow,
  Grid,
  GridCell,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  NoResults,
  Panel,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  TruncateAndTooltip
} from 'mailup-web-components'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import Pagination from './Pagination'

const MessageContent = ({ item }) => {
  const intl = useIntl()
  return (
    <>
      <Link
        variant={LINK_VARIANT_HIGHLIGHT}
        onClick={() => {
          notifyParent('openPreviewMainFrame', [item.Id])
        }}
        data-mu-test={`email-messages-open-email-preview-${item.Id}`}
      >
        <TruncateAndTooltip text={`${item.Subject}`} />
      </Link>

      <TruncateAndTooltip
        text={`${intl.formatMessage({ id: 'Load.MailupCommon.Id' })} ${
          item.Id
        }${item.Note ? ` - ${item.Note}` : ''}`}
      />
    </>
  )
}

const MessageSelect = ({ item, droppableId, handleConfirm, isDisabled }) => {
  const setModalEmailMessagesState = useSetRecoilState(modalEmailMessagesState)

  const handleClick = e => {
    setModalEmailMessagesState(prev => ({
      ...prev,
      selectedItem: item
    }))
    handleConfirm(e, item, droppableId)
  }
  return (
    <>
      <ButtonAction
        type={ACTION_BUTTON_TYPE_SELECT}
        disabled={isDisabled}
        onClick={handleClick}
        data-mu-test={`email-messages-button-confirm-${item.Id}`}
      />
    </>
  )
}

const TableMessages = ({ handleConfirm }) => {
  const intl = useIntl()
  const { TimeZoneCode: tzcode } = storeGet(STORE_KEY_LIST)
  const EXTRA_COLUMN_AFTER_WIDTH = '26px'
  const [{ orderBy, order }, setSearchState] = useRecoilState(
    modalEmailMessagesSearchState
  )

  const {
    loaded,
    items,
    droppableId,
    loading,
    selectedMessages,
    selectedItem
  } = useRecoilValue(modalEmailMessagesState)

  const handleChangeColumnsOrder = field => {
    setSearchState(prev => ({
      ...prev,
      orderBy: field,
      order: prev.order === 'DESC' ? 'ASC' : 'DESC'
    }))
  }

  const newCols = useMemo(() => tableColumns(intl), [intl])

  return (
    <>
      <>
        {loaded && items.length > 0 && (
          <Panel>
            <DataListHeader
              heads={newCols}
              afterWidth={EXTRA_COLUMN_AFTER_WIDTH}
              orderByStatus={orderBy}
              orderDescendingStatus={order === 'DESC'}
              handleChangeColumnsOrderCallback={handleChangeColumnsOrder}
            />
            <div>
              {items.map((item, key) => (
                <DataListRow key={item.Id}>
                  <Grid cols={12}>
                    <GridCell span={newCols[0].span}>
                      {loading && (
                        <SkeletonLoader
                          variant={SKELETON_LOADER_VARIANT_ONWHITE}
                        />
                      )}
                      {!loading && <MessageContent item={item} />}
                    </GridCell>
                    <GridCell span={newCols[1].span}>
                      {loading && (
                        <SkeletonLoader
                          variant={SKELETON_LOADER_VARIANT_ONWHITE}
                        />
                      )}
                      {!loading && (
                        <>
                          <p
                            data-mu-test={`sms-messages-item-last-updated-date-${item.Id}`}
                          >
                            {item.LastUpdated &&
                              fromUTCtoZoneFormatted(
                                item.LastUpdated,
                                tzcode,
                                'YYYY-MM-DD'
                              )}
                          </p>
                          <p
                            data-mu-test={`sms-messages-item-last-updated-time-${item.Id}`}
                          >
                            {item.LastUpdated &&
                              fromUTCtoZoneFormatted(
                                item.LastUpdated,
                                tzcode,
                                'HH:mm'
                              )}
                          </p>
                        </>
                      )}
                    </GridCell>
                  </Grid>
                  <DataListExtraColumn after width={EXTRA_COLUMN_AFTER_WIDTH}>
                    {loading && (
                      <SkeletonLoader
                        variant={SKELETON_LOADER_VARIANT_ONWHITE}
                      />
                    )}
                    {!loading && (
                      <MessageSelect
                        droppableId={droppableId}
                        item={item}
                        handleConfirm={handleConfirm}
                        isDisabled={
                          !!selectedMessages.find(
                            e => e.IdMessaggio === item.Id
                          ) || selectedItem
                          // && selectedItem.Id === item.Id
                        }
                      />
                    )}
                  </DataListExtraColumn>
                </DataListRow>
              ))}
            </div>
            <Pagination />
          </Panel>
        )}
      </>
      {loaded && items.length === 0 && (
        <NoResults
          text={intl.formatMessage({
            id: 'Load.Common.noResultsTitle'
          })}
        />
      )}
    </>
  )
}

TableMessages.propTypes = {
  handleConfirm: PropTypes.func
}

MessageContent.propTypes = {
  item: PropTypes.object
}

MessageSelect.propTypes = {
  item: PropTypes.object,
  handleConfirm: PropTypes.func,
  droppableId: PropTypes.string,
  isDisabled: PropTypes.false
}

export default TableMessages
